import { useMemo, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { toLocalDateTime, formatIsoDateShort } from "./Data/Date";
import { Button } from "./Controls/Button";
import { Button as BsButton } from "react-bootstrap";
import { formatAuditValue, isAddRemoveEvent, isHiddenAuditField, isNewValueOnlyField } from "./helpers/AuditLogsHelper";

export function SubscriptionAuditLogModal({ show, onHide, auditEntries }) {
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");

  const filtered = useMemo(() => {
    let list = [...auditEntries];

    if (from) {
      list = list.filter((x) => new Date(x.timestamp) >= new Date(from));
    }

    if (to) {
      const end = new Date(to + "T23:59:59.999Z");
      list = list.filter((x) => new Date(x.timestamp) <= end);
    }

    return list.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [auditEntries, from, to]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Audit Log</Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{ maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" }}
      >
        <div className="d-flex flex-md-nowrap flex-wrap gap-2 mb-3">
          <Form.Control
            className="flex-grow-1"
            type="date"
            value={from}
            onChange={(e) => {
              const newFrom = e.target.value;
              setFrom(newFrom);
              if (to && to < newFrom) setTo(newFrom);
            }}
          />

          <Form.Control
            className="flex-grow-1"
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
          />

          <BsButton
            variant="outline-secondary"
            className="rounded-4 w-100 w-md-auto"
            onClick={() => {
              setFrom("");
              setTo("");
            }}
          >
            Clear
          </BsButton>
        </div>

        {filtered.length === 0 && (
          <div className="text-muted">No audit entries found.</div>
        )}

        {filtered.map((entry, i) => (
          <AuditRecordCard key={i} entry={entry} />
        ))}
      </Modal.Body>

      <Modal.Footer>
        <Button className="w-100" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function AuditRecordCard({ entry }) {
  const isAddRemove = isAddRemoveEvent(entry.eventName);

  const isParkerSpotChange =
    entry.eventName === "SubscriptionParkerSpotQuantityChangedEvent";

  const isIntegrationEnroll = entry.eventName === "EnrollIntegrationEvent";
  const isIntegrationUnroll = entry.eventName === "UnrollIntegrationEvent";
  const isIntegrationCredentialUpdated =
    entry.eventName === "IntegrationCredentialUpdatedAuditEvent";

  const isParkGuardSuccess =
    entry.eventName === "ParkGuardSubscriptionSuccessEvent";
  const isParkGuardFail =
    entry.eventName === "ParkGuardSubscriptionFailedEvent";

  const isContractRemoved =
    entry.eventName === "SubscriptionGroupContractRemovedEvent";

  const isContractAdded =
    entry.eventName === "SubscriptionGroupContractSavedEvent";

  const isSubParkerRemoved = entry.eventName === "SubParkerRemovedEvent";
  const isSubParkerCancelScheduled = entry.eventName === "SubParkerCancelScheduledEvent";
  const subParkerCancelDate = entry.newValues?.RemovedWhen
    ? formatIsoDateShort(String(entry.newValues.RemovedWhen))
    : null;

  let integrationName = "";

  if (isParkGuardSuccess || isParkGuardFail) {
    return (
      <div className="p-3 mb-3 rounded border border-secondary">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start mb-3 gap-1">
          <div
            className="fw-bold"
            style={{
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
          >
            {toLocalDateTime(entry.timestamp)} • {entry.eventName}
          </div>
          <div
            className="text-muted small d-md-none"
            style={{
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
          >
            {entry.changedBy}
          </div>
          <div className="text-muted small d-none d-md-block">
            {entry.changedBy}
          </div>
        </div>

        {isParkGuardSuccess && (
          <div className="fw-semibold">✓ Park Guard added</div>
        )}

        {isParkGuardFail && (
          <div className="fw-semibold">✕ Park Guard failed</div>
        )}
      </div>
    );
  }

  if (isIntegrationEnroll || isIntegrationUnroll) {
    const raw =
      entry.newValues?.IntegrationType || entry.newValues?.IntegrationName;
    integrationName = raw ?? "Integration";
  }

  return (
    <div className="p-3 mb-3 rounded border border-secondary">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start mb-3 gap-1">
        <div
          className="fw-bold"
          style={{
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {toLocalDateTime(entry.timestamp)} • {entry.eventName}
        </div>
        <div
          className="text-muted small d-md-none"
          style={{
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {entry.changedBy}
        </div>
        <div className="text-muted small d-none d-md-block">
          {entry.changedBy}
        </div>
      </div>

      {shouldShowParkerCard(
        entry,
        isParkerSpotChange,
        isIntegrationEnroll,
        isIntegrationUnroll,
        isIntegrationCredentialUpdated
      ) && (
        <div className="mb-3">
          <div className="fw-semibold mb-1">Parker</div>
          <div
            className={
              "fw-semibold p-2 rounded " +
              (isIntegrationEnroll
                ? "bg-success bg-opacity-25 text-success"
                : isIntegrationUnroll
                  ? "bg-danger bg-opacity-25 text-danger"
                  : "bg-secondary bg-opacity-10")
            }
          >
            {entry.newValues.ParkerName}
          </div>
        </div>
      )}

      {(isIntegrationEnroll || isIntegrationUnroll) && (
        <div className="fw-semibold">
          {isIntegrationEnroll ? "✓ Added" : "✕ Removed"} {integrationName}
        </div>
      )}

      {isContractAdded && (
        <div className="mb-3">
          <div className="fw-semibold text-success mb-2">
            ✓ Contract updated to group {entry.entityName}
          </div>

          {entry.newValues?.rules &&
            Array.isArray(entry.newValues.rules) &&
            entry.newValues.rules.map((rule, idx) => {
              const oldRule =
                entry.oldValues?.rules && Array.isArray(entry.oldValues.rules)
                  ? entry.oldValues.rules[idx] || {}
                  : {};

              const hasChanged =
                rule.Min !== oldRule.Min ||
                rule.Max !== oldRule.Max ||
                rule.Price !== oldRule.Price ||
                rule.Type !== oldRule.Type;

              if (!hasChanged) return null;

              return (
                <div key={idx} className="mb-4">
                  <div className="fw-semibold mb-2">Rule {idx + 1}</div>

                  <div className="row g-2">
                    <div className="col-6">
                      <div className="small text-muted mb-1">Previous</div>
                      <div className="p-2 rounded bg-danger bg-opacity-25 text-danger text-decoration-line-through">
                        <div>
                          <strong>Min:</strong>{" "}
                          {oldRule?.Min !== undefined ? oldRule.Min : "-"}
                        </div>
                        <div>
                          <strong>Max:</strong>{" "}
                          {oldRule?.Max !== undefined ? oldRule.Max : "-"}
                        </div>
                        <div>
                          <strong>Price:</strong>{" "}
                          {oldRule?.Price !== undefined ? oldRule.Price : "-"}
                        </div>
                        <div>
                          <strong>Type:</strong>{" "}
                          {oldRule?.Type !== undefined ? oldRule.Type : "-"}
                        </div>
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="small text-muted mb-1">Updated</div>
                      <div className="p-2 rounded bg-success bg-opacity-25 text-success">
                        <div>
                          <strong>Min:</strong> {rule.Min}
                        </div>
                        <div>
                          <strong>Max:</strong> {rule.Max}
                        </div>
                        <div>
                          <strong>Price:</strong> {rule.Price}
                        </div>
                        <div>
                          <strong>Type:</strong> {rule.Type}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {isContractRemoved && (
        <div className="fw-semibold">
          ✕ Contract removed from group
          {entry.entityName ? ` ${entry.entityName}` : ""}
        </div>
      )}

      {isSubParkerCancelScheduled && subParkerCancelDate && (
        <div className="fw-semibold">
          {`✕ ${entry.entityName} scheduled to cancel on ${subParkerCancelDate}`}
        </div>
      )}

      {isAddRemove && !isContractRemoved && !isContractAdded && !isSubParkerCancelScheduled && (
        <div className="fw-semibold">
          {entry.eventName.includes("Created")
            ? `✓ ${entry.entityName} was added`
            : `✕ ${entry.entityName} was removed`}
        </div>
      )}

      {shouldShowChanges({
        isAddRemove,
        isIntegrationEnroll,
        isIntegrationUnroll,
      }) &&
        !isContractAdded &&
        entry.changes
          .filter(
            (c) =>
              !isHiddenAuditField(c.field) &&
              !(
                isIntegrationCredentialUpdated && c.field === "ParkerName"
              )
          )
          .map((c) => {
          const newValueOnly = isNewValueOnlyField(c.field);
          return (
            <div key={c.field} className="mb-4">
              <div className="fw-semibold mb-2">{c.field}</div>

              <div className="row g-2">
                {!newValueOnly && (
                  <div className="col-6">
                    <div className="small text-muted mb-1">Previous</div>
                    <div className="p-2 rounded bg-danger bg-opacity-25 text-danger text-decoration-line-through">
                      {formatAuditValue(c.oldValue, c.field)}
                    </div>
                  </div>
                )}

                <div className={newValueOnly ? "col-12" : "col-6"}>
                  {!newValueOnly && (
                    <div className="small text-muted mb-1">Updated</div>
                  )}
                  <div className="p-2 rounded bg-success bg-opacity-25 text-success">
                    {formatAuditValue(c.newValue, c.field)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
}

function shouldShowParkerCard(
  entry,
  isParkerSpotChange,
  isIntegrationEnroll,
  isIntegrationUnroll,
  isIntegrationCredentialUpdated
) {
  return (
    (isParkerSpotChange ||
      isIntegrationEnroll ||
      isIntegrationUnroll ||
      isIntegrationCredentialUpdated) &&
    entry?.newValues?.ParkerName
  );
}

const shouldShowChanges = ({
  isAddRemove,
  isIntegrationEnroll,
  isIntegrationUnroll,
}) => {
  return !isAddRemove && !isIntegrationEnroll && !isIntegrationUnroll;
};
